import React from "react";

import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/apps/shared/components/dialog/dialog";
import { ErrorBoundary } from "~/apps/shared/components/error-boundary/error-boundary";
import {
  colorsByAgencyNoticeType,
  iconsByAgencyNoticeType,
} from "~/apps/shared/constants";

import { Button } from "@toolkit/v2";

import { useLanguage } from "../../contexts/language.context";
import {
  AgencyNotice,
  AgencyNoticeModel,
} from "../../models/agency-notice.model";
import { styles } from "./agency-notices-dialog.styles";
import { useAgencyNoticesDialogTranscription } from "./agency-notices-dialog.transcription";
import { useAgencyNoticesDialogViewModel } from "./agency-notices-dialog.view-model";

type Props = Pick<React.ComponentPropsWithoutRef<typeof Dialog>, "open"> & {
  agencyNotices: AgencyNotice[];
  onClose: () => void;
  onDismiss: (token: string) => void;
};

const Component: React.FC<Props> = (props) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { agencyNotices, open } = props;

  const {
    activeAgencyNotice,
    handleBack,
    handleClose,
    handleContinue,
    isIndexActiveIndex,
    shouldDisableBackButton,
    shouldDisableContinueButton,
  } = useAgencyNoticesDialogViewModel(props);

  const { transcription } = useAgencyNoticesDialogTranscription();

  const { languageCode } = useLanguage();

  if (!activeAgencyNotice) {
    return null;
  }

  const activeAgencyNoticeModel = new AgencyNoticeModel(activeAgencyNotice);

  const content = activeAgencyNoticeModel.getAppropriateContentBody(
    languageCode,
  );
  const title = activeAgencyNoticeModel.getAppropriateContentTitle(
    languageCode,
  );

  return (
    <Dialog
      css={styles.root}
      disableEscapeKeyDown
      onClose={(_, reason) => {
        if (reason === "backdropClick") {
          return;
        }

        handleClose();
      }}
      open={open}
    >
      <div css={styles.top.root}>
        <DialogHeader
          color={colorsByAgencyNoticeType[activeAgencyNotice.type].color}
          icon={iconsByAgencyNoticeType[activeAgencyNotice.type]}
        />
        <DialogContent css={styles.top.content.root}>
          {title ? (
            <DialogTitle css={styles.top.content.title}>{title}</DialogTitle>
          ) : null}
          <p css={styles.top.content.body}>{content}</p>
          <span css={styles.top.content.timestamp}>
            {activeAgencyNoticeModel.getFormattedCreatedAt(languageCode)}
          </span>
        </DialogContent>
      </div>
      <DialogFooter css={styles.footer.root}>
        {agencyNotices.length > 1 ? (
          <div css={styles.footer.left.root}>
            {agencyNotices.map((_, index) => (
              <div
                css={styles.footer.left.indicator({
                  isActive: isIndexActiveIndex(index),
                })}
                key={index}
              />
            ))}
          </div>
        ) : null}
        <div css={styles.footer.right.root}>
          {agencyNotices.length > 1 ? (
            <button
              css={styles.footer.right.back}
              disabled={shouldDisableBackButton}
              onClick={handleBack}
              type="button"
            >
              {transcription.back}
            </button>
          ) : null}
          <Button
            css={styles.footer.right.continue}
            disabled={shouldDisableContinueButton}
            onClick={handleContinue}
            type="button"
          >
            {transcription.continue}
          </Button>
        </div>
      </DialogFooter>
    </Dialog>
  );
};

export const AgencyNoticesDialog: React.FC<Props> = (props) => {
  return (
    <ErrorBoundary fallback={() => <></>}>
      <Component {...props} />
    </ErrorBoundary>
  );
};
